import { useCallback, useState } from 'react';
import { config } from '../config/env';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * Do an authenticated request to the Inspect-Action API.
 */
export const useApiFetch = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { getValidToken } = useAuthContext();

  const apiFetch = useCallback(
    async (url: string, request?: RequestInit) => {
      setIsLoading(true);
      setError(null);
      try {
        const token = await getValidToken();
        if (!token) {
          throw new Error('No valid token available for fetching permalink');
        }

        url = url.startsWith('/') ? config.apiBaseUrl + url : url;

        const response = await fetch(url, {
          ...request,
          headers: {
            Authorization: `Bearer ${token}`,
            ...request?.headers,
          },
        });
        if (!response.ok) {
          throw new Error(
            `API request failed: ${response.status} ${response.statusText}`
          );
        }
        return response;
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return null;
        }
        setError(err as Error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [getValidToken]
  );

  return { apiFetch, isLoading, error };
};
